<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search recordings..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      :active-filter-count="activeFilterCount"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchRecordings"
      @page-change="goToPage"
      @clear-filters="clearFilters"
    >
      <template #filters>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Status</label>
          <select v-model="filters.status">
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="recording">Recording</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Tag</label>
          <TagSelect v-model="filters.tags" placeholder="Filter by tags" :allow-create="false" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Category</label>
          <CategorySelect ref="categorySelect" v-model="filters.category_id" placeholder="All categories" preload />
        </div>
      </template>
    </ListToolbar>

    <!-- Table -->
    <!-- Batch action bar -->
    <div v-if="selectedIds.length" class="flex items-center justify-between px-5 py-3 mb-3 rounded-xl bg-[var(--color-primary-light)] border border-[var(--color-primary-glow)]">
      <span class="text-sm font-medium text-[var(--color-primary)]">{{ selectedIds.length }} selected</span>
      <div class="flex items-center gap-2">
        <button @click="selectedIds = []" class="btn-secondary !text-xs !px-3 !py-1.5">Clear</button>
        <button @click="confirmBatchDelete" class="btn-danger !text-xs !px-3 !py-1.5">
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
          Delete Selected
        </button>
      </div>
    </div>

    <div v-if="loading || items.length" :class="{ 'animate-fade-in': !loading }" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="w-12 pl-5 pr-0 py-3.5">
              <button @click="toggleSelectAll" :class="['w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors', allSelected ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-gray-300 hover:border-gray-400']">
                <svg v-if="allSelected" class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else-if="selectedIds.length" class="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
            </th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Name</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Started</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Ended</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Duration</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden sm:table-cell">Tags</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Category</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="i in 8" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="w-12 pl-5 pr-0 py-4"><div class="w-5 h-5 bg-gray-100 rounded-md skeleton-pulse" /></td>
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-gray-100 skeleton-pulse shrink-0" />
                <div class="space-y-2 flex-1">
                  <div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" />
                  <div class="h-3 bg-gray-50 rounded skeleton-pulse w-16" />
                </div>
              </div>
            </td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-20" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-20" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-16" /></td>
            <td class="px-6 py-4 hidden sm:table-cell"><div class="h-6 bg-gray-100 rounded-md skeleton-pulse w-10" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-6 bg-gray-100 rounded-md skeleton-pulse w-10" /></td>
            <td class="px-6 py-4"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-8 ml-auto" /></td>
          </tr>
        </tbody>
        <tbody v-else class="divide-y divide-gray-50">
          <tr v-for="item in items" :key="item._id" :class="['transition-colors', selectedIds.includes(item._id) ? 'bg-[var(--color-primary-light)]' : 'hover:bg-gray-50/50']">
            <td class="w-12 pl-5 pr-0 py-4">
              <button @click="toggleSelect(item._id)" :class="['w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors', selectedIds.includes(item._id) ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-gray-300 hover:border-gray-400']">
                <svg v-if="selectedIds.includes(item._id)" class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
            </td>
            <!-- Name + status -->
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <router-link v-if="item.status !== 'recording'" :to="{ name: 'editor', query: { rId: item._id } }" class="text-[14px] text-gray-900 hover:text-[var(--color-primary)] transition-colors truncate block">{{ item.title || 'Untitled' }}</router-link>
                  <span v-else class="text-[14px] text-gray-900 truncate block">{{ item.title || 'Untitled' }}</span>
                  <div class="flex items-center gap-2 mt-1">
                    <span :class="statusClass(item)">{{ item.status || 'processing' }}</span>
                  </div>
                </div>
              </div>
            </td>

            <!-- Started -->
            <td class="px-6 py-4 hidden lg:table-cell">
              <span class="text-sm text-gray-500">{{ $filters.formatDatetime(item.started_at || item.created_at) }}</span>
            </td>

            <!-- Ended -->
            <td class="px-6 py-4 hidden lg:table-cell">
              <span class="text-sm text-gray-500">{{ item.ended_at ? $filters.formatDatetime(item.ended_at) : '—' }}</span>
            </td>

            <!-- Duration -->
            <td class="px-6 py-4 hidden lg:table-cell">
              <span class="text-sm font-data text-gray-500">{{ calcDuration(item) }}</span>
            </td>

            <!-- Tags -->
            <td class="px-6 py-4 hidden sm:table-cell">
              <button
                v-if="item.tags && item.tags.length"
                @click="openQuickTags(item)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium bg-[var(--color-primary-light)] text-[var(--color-primary)] hover:bg-[var(--color-primary-glow)] transition-colors"
              >
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                {{ item.tags.length }}
              </button>
              <button v-else @click="openQuickTags(item)" class="text-sm text-gray-400 hover:text-gray-500 transition-colors">+ Add</button>
            </td>

            <!-- Category -->
            <td class="px-6 py-4 hidden md:table-cell">
              <button
                v-if="item.categories && item.categories.length"
                @click="openQuickCategory(item)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium bg-violet-50 text-violet-500 hover:bg-violet-100 transition-colors"
              >
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                {{ item.categories.length }}
              </button>
              <button v-else @click="openQuickCategory(item)" class="text-sm text-gray-400 hover:text-gray-500 transition-colors">+ Add</button>
            </td>

            <!-- Actions -->
            <td class="px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1">
                <ActionsMenu :items="recordingActions(item)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="!loading && (search || filters.status || filters.tags?.length || filters.category_id)" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching recordings found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
    </div>

    <!-- Empty: no records -->
    <div v-else-if="!loading" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/>
        </svg>
      </div>
      <p class="text-base font-medium text-gray-900">No recordings found</p>
      <p class="text-sm text-gray-500 mt-1">Recordings will appear here when streams are recorded</p>
    </div>

    <!-- Quick edit tags -->
    <QuickEditModal :show="showQuickTags" title="Edit Tags" :saving="quickSaving" @close="showQuickTags = false" @save="saveQuickTags">
      <TagSelect v-model="quickTagIds" />
    </QuickEditModal>

    <!-- Hidden lookups (always mounted for name resolution) -->
    <CategorySelect ref="categoryLookup" class="hidden" preload />
    <TagSelect ref="tagLookup" class="hidden" preload />

    <!-- Quick edit category -->
    <QuickEditModal :show="showQuickCategory" title="Edit Categories" :saving="quickSaving" @close="showQuickCategory = false" @save="saveQuickCategory">
      <CategorySelect v-model="quickCategoryIds" />
    </QuickEditModal>

    <!-- Preview modal -->
    <MediaPlayerModal
      :show="showPlayer"
      :title="previewItem?.title || 'Recording'"
      :hls-url="previewItem ? $mediaUrl.recording(previewItem._id) : ''"
      :formats="[]"
      @close="showPlayer = false; previewItem = null"
    />

    <!-- Delete confirm -->
    <ConfirmModal
      :show="showDeleteModal" :loading="deleting"
      title="Delete Recording"
      :message="`Delete '${deleteItem?.title}'? This cannot be undone.`"
      confirm-text="Delete"
      :danger="true"
      @confirm="deleteRecording"
      @cancel="showDeleteModal = false"
    />

    <ConfirmModal
      :show="showBatchDeleteModal"
      :loading="batchDeleting"
      title="Delete Selected Recordings"
      :message="`Delete ${selectedIds.length} recording(s)? This cannot be undone.`"
      confirm-text="Delete All"
      :danger="true"
      @confirm="batchDelete"
      @cancel="showBatchDeleteModal = false"
    />
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import icons from '../../components/action-icons.js'
import ConfirmModal from '../../components/ConfirmModal.vue'
import MediaPlayerModal from '../../components/MediaPlayerModal.vue'
import QuickEditModal from '../../components/QuickEditModal.vue'
import TagSelect from '../../components/TagSelect.vue'
import CategorySelect from '../../components/CategorySelect.vue'
import api from '../../services/api.js'
import { notifySuccess } from '../../services/notifications.js'

export default {
  name: 'RecordingList',
  components: { ListToolbar, ActionsMenu, ConfirmModal, MediaPlayerModal, QuickEditModal, TagSelect, CategorySelect },

  data() {
    return {
      loading: true,
      items: [],
      search: '',
      filters: { status: 'success', tags: [], category_id: null },
      sort: 'created:-1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' },
        { value: 'name:-1', label: 'Name Z-A' },
        { value: 'status:1', label: 'Status A-Z' },
        { value: 'status:-1', label: 'Status Z-A' },
        { value: 'created:1', label: 'Created (oldest)' },
        { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 25, page: 1, total: 0 },
      showPlayer: false,
      previewItem: null,
      showDeleteModal: false, deleting: false,
      deleteItem: null,
      showQuickTags: false,
      showQuickCategory: false,
      quickSaving: false,
      quickEditItem: null,
      quickTagIds: [],
      quickCategoryIds: [],
      selectedIds: [],
      showBatchDeleteModal: false,
      batchDeleting: false
    }
  },

  computed: {
    activeFilterCount() {
      return Object.values(this.filters).filter(v => Array.isArray(v) ? v.length > 0 : v !== '' && v != null).length
    },
    allSelected() {
      return this.items.length > 0 && this.selectedIds.length === this.items.length
    },
  },

  watch: {
    search() { this.pagination.page = 1; this.fetchRecordings() },
    sort() { this.pagination.page = 1; this.fetchRecordings() },
    filters: { deep: true, handler() { this.pagination.page = 1; this.fetchRecordings() } }
  },

  mounted() {
    this.fetchRecordings()
  },

  methods: {
    async fetchRecordings(count = true) {
      try {
        const params = { limit: this.pagination.limit, page: this.pagination.page }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.tags?.length) params.tags = this.filters.tags.join(',')
        if (this.filters.category_id) params.categories = this.filters.category_id
        if (count) params.count = true
        const res = await api.get('/recording', { params })
        this.items = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
        this.selectedIds = []
      } catch { /* keep existing */ } finally { this.loading = false }
    },

    goToPage(page) {
      this.pagination.page = page
      this.fetchRecordings(false)
    },

    clearFilters() {
      this.filters = { status: '', tags: [], category_id: null }
    },


    getTagsList(tags) {
      if (!tags?.length) return []
      return tags.map(t => {
        if (typeof t === 'object' && t.name) return t
        const id = typeof t === 'object' ? t._id?.toString() : t?.toString()
        const name = this.$refs.tagLookup?.getTagName(id) || id
        return { _id: id, name }
      })
    },

    getCategoryName(id) {
      if (!id) return ''
      return this.$refs.categoryLookup?.getCategoryName(id) || ''
    },

    openQuickTags(item) {
      this.quickEditItem = item
      this.quickTagIds = (item.tags || []).map(t => typeof t === 'string' ? t : t._id)
      this.showQuickTags = true
    },

    async saveQuickTags() {
      if (!this.quickEditItem) return
      this.quickSaving = true
      try {
        await api.patch(`/recording/${this.quickEditItem._id}`, { tags: this.quickTagIds })
        notifySuccess('Tags updated')
        this.showQuickTags = false
        this.fetchRecordings()
      } catch { /* toast */ } finally { this.quickSaving = false }
    },

    openQuickCategory(item) {
      this.quickEditItem = item
      this.quickCategoryIds = (item.categories || []).map(c => typeof c === 'string' ? c : c._id?.toString())
      this.showQuickCategory = true
    },

    async saveQuickCategory() {
      if (!this.quickEditItem) return
      this.quickSaving = true
      try {
        await api.patch(`/recording/${this.quickEditItem._id}`, { categories: this.quickCategoryIds })
        notifySuccess('Categories updated')
        this.showQuickCategory = false
        this.fetchRecordings()
      } catch { /* toast */ } finally { this.quickSaving = false }
    },

    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx >= 0) this.selectedIds.splice(idx, 1)
      else this.selectedIds.push(id)
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIds = []
      } else {
        this.selectedIds = this.items.map(i => i._id)
      }
    },

    confirmBatchDelete() {
      this.showBatchDeleteModal = true
    },

    async batchDelete() {
      this.batchDeleting = true
      try {
        await Promise.all(this.selectedIds.map(id => api.delete(`/recording/${id}`)))
        notifySuccess(`${this.selectedIds.length} recording(s) deleted`)
        this.selectedIds = []
        this.showBatchDeleteModal = false
        this.fetchRecordings()
      } catch {
        notifySuccess('Some recordings could not be deleted')
      } finally { this.batchDeleting = false }
    },

    calcDuration(item) {
      const ms = item.duration
      if (!ms) return '—'
      const s = Math.floor(ms / 1000)
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    },

    statusClass(item) {
      const s = item.status
      if (s === 'success') return 'badge-live'
      if (s === 'failed') return 'badge-error'
      if (['processing', 'transcoding', 'recording'].includes(s)) return 'badge-warning'
      return 'badge-idle'
    },

    openPreview(item) {
      this.previewItem = item
      this.showPlayer = true
    },

    recordingActions(item) {
      return [
        { label: 'Preview', show: item.status === 'success' && !!item.url, action: () => this.openPreview(item), icon: icons.preview },
        { label: 'Open in Editor', show: item.status !== 'recording', to: { path: '/editor', query: { rId: item._id } }, icon: icons.editor },
        { divider: true },
        { label: 'Delete', show: this.$checkPermission('recording.delete'), danger: true, action: () => this.confirmDelete(item), icon: icons.delete }
      ]
    },

    confirmDelete(item) {
      this.deleteItem = item
      this.showDeleteModal = true
    },

    async deleteRecording() {
      if (!this.deleteItem) return
      this.deleting = true
      try {
        await api.delete(`/recording/${this.deleteItem._id}`)
        notifySuccess('Recording deleted')
        this.showDeleteModal = false
        this.deleteItem = null
        this.fetchRecordings()
      } catch { /* toast */ } finally { this.deleting = false }
    }
  }
}
</script>
